/**
 * RetirementEventForm Component
 * WP-RETIRE-8: Create/edit life events
 * WP-PLAN-1 Phase 3: the two model fields are now DERIVED from one plain
 * question instead of being asked directly.
 *
 * Fields:
 * - kind (radio: one of the four EVENT_KIND_OPTIONS) -> derives eventType and
 *   the sign of amountCents
 * - name (text)
 * - amountCents (dollar input, unsigned - the sign comes from `kind`)
 * - year (number, 2000-2100)
 * - endYear (number, shown only for the "every year" kinds, must be >= year)
 *
 * The submitted payload is UNCHANGED: { name, eventType, amountCents (signed),
 * year, endYear? }. Only the questions asked to produce it changed.
 *
 * The chooser is FIRST because it determines the labels of every later field
 * ("Amount per year" vs "Amount"), so a screen-reader user hears the context
 * before the fields it governs.
 *
 * On CREATE nothing is preselected, deliberately. The old form defaulted to
 * one-time income, so a user who did not notice the controls could record a
 * cost as money coming in and the projection would quietly be wrong in their
 * favour. The choice is the point of the form, so it is required.
 */

import { useState } from 'react';
import Input from '@/components/ui/Input';
import { dollarsToCents, centsToDollars } from '@/utils/format';
import type {
  RetirementEvent,
  RetirementEventCreate,
} from '@/types/retirement';
import {
  ASSUMPTIONS_INCOME_HINT,
  EVENT_KIND_OPTIONS,
  eventKindOf,
  eventKindOption,
  type EventKind,
} from './eventKind';

interface RetirementEventFormProps {
  /** If provided, form is in edit mode */
  event?: RetirementEvent;
  /** Called with the created/updated event data */
  onSubmit: (data: RetirementEventCreate) => void;
  /** Called when user cancels */
  onCancel: () => void;
  /** Whether the form is currently submitting */
  isSubmitting?: boolean;
}

export default function RetirementEventForm({
  event,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: RetirementEventFormProps) {
  const isEdit = !!event;

  // Initialize form state from event (edit) or defaults (create)
  const [name, setName] = useState(event?.name ?? '');

  // On edit, derive the choice from the stored event - the mapping is a
  // bijection, so there is exactly one. On create, null: see the header.
  const [kind, setKind] = useState<EventKind | null>(
    event ? eventKindOf(event.eventType, event.amountCents) : null
  );
  const selected = kind ? eventKindOption(kind) : null;
  const isRecurring = selected?.eventType === 'cash_flow';

  const [dollarAmount, setDollarAmount] = useState(() => {
    if (event) {
      return Math.abs(centsToDollars(event.amountCents));
    }
    return 0;
  });
  const [year, setYear] = useState(event?.year ?? new Date().getFullYear());
  const [endYear, setEndYear] = useState(event?.endYear ?? year + 5);

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    if (!selected) {
      newErrors.kind = 'Choose what kind of event this is';
    }

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (dollarAmount <= 0) {
      newErrors.amount = 'Amount must be greater than zero';
    }

    if (year < 2000 || year > 2100) {
      newErrors.year = 'Year must be between 2000 and 2100';
    }

    if (isRecurring) {
      if (endYear < year) {
        newErrors.endYear = 'End year must be on or after start year';
      }
      if (endYear < 2000 || endYear > 2100) {
        newErrors.endYear = 'End year must be between 2000 and 2100';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    // validate() guarantees `selected` is set; this narrows it for TypeScript.
    if (!selected) return;

    const signedAmountCents =
      dollarsToCents(dollarAmount) * (selected.isIncome ? 1 : -1);

    // Payload shape unchanged from WP-RETIRE-8. eventType and the sign are now
    // derived from `selected` rather than read from two separate controls.
    const data: RetirementEventCreate = {
      name: name.trim(),
      eventType: selected.eventType,
      amountCents: signedAmountCents,
      year,
      endYear: selected.eventType === 'cash_flow' ? endYear : undefined,
    };

    onSubmit(data);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* What kind of event - derives eventType and the sign of the amount.
          Native fieldset + radios on purpose: that gives arrow-key navigation,
          a single tab stop and correct announcements without any ARIA. */}
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-1">
          What kind of event is this?
        </legend>
        {/* Steers regular retirement income to the Assumptions region. The
            projection adds assumption income AND events, so entering Social
            Security in both places counts it twice. */}
        <p className="mb-2 text-xs text-gray-500">{ASSUMPTIONS_INCOME_HINT}</p>
        <div className="space-y-2">
          {EVENT_KIND_OPTIONS.map((option) => (
            <label
              key={option.kind}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                kind === option.kind
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="eventKind"
                value={option.kind}
                checked={kind === option.kind}
                onChange={() => setKind(option.kind)}
                className="mt-0.5 text-primary-600 focus:ring-primary-500"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">
                  {option.label}
                </span>
                <span className="block text-xs text-gray-500">
                  {option.description}
                </span>
              </span>
            </label>
          ))}
        </div>
        {errors.kind && (
          <p className="mt-1 text-sm text-red-600">{errors.kind}</p>
        )}
      </fieldset>

      {/* Name */}
      <Input
        label="Event Name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={errors.name}
        placeholder="e.g., College for Maya, Lake house"
      />

      {/* Amount. No sign control: the sign comes from the choice above, which
          is why the old +/- toggle (two unlabelled buttons whose visible text
          disagreed with their accessible name) could be removed outright. */}
      <Input
        label={isRecurring ? 'Amount per year' : 'Amount'}
        type="number"
        min={0}
        value={dollarAmount || ''}
        onChange={(e) => setDollarAmount(parseFloat(e.target.value) || 0)}
        error={errors.amount}
        hint={
          selected
            ? selected.isIncome
              ? "How much you'll receive, in today's dollars"
              : "How much you'll pay, in today's dollars"
            : undefined
        }
      />

      {/* Year */}
      <Input
        label={isRecurring ? 'Start Year' : 'Year'}
        type="number"
        min={2000}
        max={2100}
        value={year}
        onChange={(e) => setYear(parseInt(e.target.value, 10) || 2000)}
        error={errors.year}
      />

      {/* End Year (cash_flow only) */}
      {isRecurring && (
        <Input
          label="End Year"
          type="number"
          min={year}
          max={2100}
          value={endYear}
          onChange={(e) => setEndYear(parseInt(e.target.value, 10) || year)}
          error={errors.endYear}
          hint={`Every year from ${year} through ${endYear}`}
        />
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting
            ? 'Saving...'
            : isEdit
              ? 'Save Changes'
              : 'Add Event'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
